import { greetingCardBaseUrl } from '@apps/greetingCard/GreetingCard.controller';
import { useSaveGreetingCardAndEmailDraftMutation } from '@graphql/generated';
import { EGiftWrapType, getGiftWrapThemeByID, IGreetingCardPayload } from '@shared/components/GiftWrap';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCallback } from 'react';

// Extracted from GuestRegistry.telemetry.ts.
type GiftWrapDraftSaveError = ({ thrownError, draft }: { thrownError: unknown; draft: object }) => {
  category: string;
  action: string;
  label: string;
  extraInfo: {
    thrownError: unknown;
    draft: object;
  };
};

type GiftWrapPayload = {
  message: string;
  themeId: string;
  image: string;
  store?: string | undefined;
  title: string;
  giftLink: string;
};

export const useSaveCardDraft = (giftWrapDraftSaveError?: GiftWrapDraftSaveError) => {
  const { eventInfo } = useEventInfo();
  const [saveGreetingCardDraft, { loading: savingGreetingCardDraft }] = useSaveGreetingCardAndEmailDraftMutation();
  const isSaving = savingGreetingCardDraft;

  const saveCardDraft = useCallback(
    async (eventId: string | undefined, payload: GiftWrapPayload): Promise<string | undefined> => {
      const message = payload.message || '';

      const isPremium = getGiftWrapThemeByID(payload.themeId)?.type === EGiftWrapType.PREMIUM;

      const draftValuesGreetingCard: IGreetingCardPayload = {
        type: 'giftWrap',
        baseUrl: greetingCardBaseUrl,
        eventId: eventId || '',
        cardDetails: {
          message,
          name1: isPremium ? (eventInfo?.fianceeFirstName ?? undefined) : undefined,
          name2: isPremium ? (eventInfo?.ownerFirstName ?? undefined) : undefined,
          product: {
            title: payload?.title ?? '',
            image: payload?.image,
            link: payload.giftLink,
          },
        },
        theme: payload.themeId ?? 'none',
      };

      try {
        const draftResponse = await saveGreetingCardDraft({
          variables: {
            payload: draftValuesGreetingCard,
          },
        });
        return draftResponse.data?.saveGreetingCardAndEmailDraft?.id;
      } catch (e) {
        giftWrapDraftSaveError?.({ thrownError: e, draft: draftValuesGreetingCard });
      }
      return undefined;
    },
    [eventInfo, saveGreetingCardDraft, giftWrapDraftSaveError],
  );

  return [saveCardDraft, isSaving] as const;
};
